import { useRef } from 'react'
import { batch } from 'react-redux'
import { buildCheckoutRequestItem } from '@ancon/wildcat-utils/checkout'
import { createCodedError } from '@ancon/wildcat-utils/error'
import { CheckoutItemType, ErrorCode } from '@ancon/wildcat-types'
import { CheckoutRequestItem } from '@ancon/wildcat-utils/checkout/types'

import {
  ConfigureProductFormState,
  ConfigureProductUpsellGroupFormState,
} from '../types'
import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  productsUpSellGroupIsOutOfStockSelector,
  productsUpsellGroupHasProductsSelector,
} from '../store/productSelectors'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import {
  setProductDetailsModalVisible,
  setProductUpsellModalVisible,
} from '../store/productSlice'
import { checkoutCurrentCheckoutSelectedItemIdSelector } from '../../checkout/store/checkoutSelectors'
import useAppStore from '../../../store/hooks/useAppStore'
import {
  outletListItemSelector,
  outletSelectedOutletIdSelector,
} from '../../outlet/store/outletSelector'
import {
  addCheckoutItem,
  updateCheckoutItem,
} from '../../checkout/store/checkoutThunks'
import { setSelectedCheckoutItemId } from '../../checkout/store/checkoutSlice'
import { appLanguageSelector } from '../../app/store/appSelectors'
import { appShowError } from '../../app/store/appSlice'
import { ErrorModalType } from '../../app/types'
import getTranslatedError from '../../app/utils/getTranslatedError'
import useRouteMatch from '../../app/hooks/useRouteMatch'
import { AppRoutes } from '../../app/constants'
import {
  isPreOrderCheckoutSelector,
  preOrderSelectedAttendeeIdSelector,
} from '../../preOrder/store/preOrderSelectors'
import {
  addPreOrderCheckoutItems,
  updatePreOrderCheckoutItem,
} from '../../preOrder/store/preOrderThunks'
import isOutletAvailableForOrders from '../../outlet/utils/isOutletAvailableForOrders'

import ProductDetailsModal from './ProductDetailsModal'
import ProductUpsellModal from './ProductUpsellModal'

export default function OrderProduct() {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const hasUpsellGroups = useAppSelector(productsUpsellGroupHasProductsSelector)
  const productUpsellIsOutOfStock = useAppSelector(
    productsUpSellGroupIsOutOfStockSelector,
  )
  const outlet = useAppSelector(outletListItemSelector)

  const isPreOrderManage = useRouteMatch([AppRoutes.PreOrderManage])

  const productDetailsFormValuesRef = useRef<
    ConfigureProductFormState | undefined
  >(undefined)

  function clearSelectedCheckoutItemId() {
    const selectedCheckoutItemId =
      checkoutCurrentCheckoutSelectedItemIdSelector(store.getState())

    if (selectedCheckoutItemId) {
      dispatch(setSelectedCheckoutItemId(null))
    }
  }

  async function handleAddToItemsToCart(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    upsellFormState?: ConfigureProductUpsellGroupFormState,
  ) {
    const selectedOutletId = outletSelectedOutletIdSelector(store.getState())
    const selectedCheckoutItemId =
      checkoutCurrentCheckoutSelectedItemIdSelector(store.getState())
    const isPreOrderCheckout = isPreOrderCheckoutSelector(store.getState())
    const selectedAttendeeId = preOrderSelectedAttendeeIdSelector(
      store.getState(),
    )

    if (productDetailsFormValuesRef.current) {
      const items: CheckoutRequestItem[] = []

      const { outletId, selectedVariantId, ...productDetailsFormValues } =
        productDetailsFormValuesRef.current

      const checkoutRequestItem = buildCheckoutRequestItem({
        ...productDetailsFormValues,
        selectedVariantId: selectedVariantId!,
        outletId,
      })

      items.push(checkoutRequestItem)

      if (upsellFormState && upsellFormState.selectedUpsellGroups?.length) {
        const { selectedUpsellGroups, upsellGroupId } = upsellFormState
        const upsellSelectedProducts = selectedUpsellGroups.reduce<
          CheckoutRequestItem[]
        >((acc, product) => {
          if (product.selectedCount > 0) {
            acc.push({
              variantId: product.variantId,
              quantity: product.selectedCount,
              modifications: [],
              upsellGroupId,
              addOnProducts: [],
              itemType: CheckoutItemType.UpsellProduct,
            })
          }
          return acc
        }, [])

        items.push(...upsellSelectedProducts)
      }

      try {
        if (selectedCheckoutItemId) {
          if (isPreOrderCheckout) {
            await dispatch(
              updatePreOrderCheckoutItem({
                updatedItem: items[0],
                checkoutItemId: selectedCheckoutItemId,
                attendeeId: selectedAttendeeId!,
              }),
            )
          } else {
            await dispatch(
              updateCheckoutItem({
                item: items[0],
                checkoutItemId: selectedCheckoutItemId,
                outletId: outletId || selectedOutletId,
              }),
            )
          }
        } else if (isPreOrderCheckout) {
          await dispatch(
            addPreOrderCheckoutItems({
              items,
              attendeeId: selectedAttendeeId!,
            }),
          )
        } else {
          await dispatch(
            addCheckoutItem({
              items,
              outletId: outletId || selectedOutletId,
            }),
          )
        }
        productDetailsFormValuesRef.current = undefined
      } catch (err) {
        // TODO: handle error
      }

      clearSelectedCheckoutItemId()
    }

    productDetailsFormValuesRef.current = undefined
  }

  async function handleProductDetailsModalSubmit(
    values: ConfigureProductFormState,
  ) {
    if (!isPreOrderManage && outlet && !isOutletAvailableForOrders(outlet)) {
      const locale = appLanguageSelector(store.getState())
      const error = await getTranslatedError(
        createCodedError(
          'Restaurant not open yet',
          ErrorCode.RequestedOutletIsNotOpen,
        ),
        undefined,
        locale,
      )
      dispatch(
        appShowError({
          ...error,
          errorModalType: ErrorModalType.OutletNotAvailableForOrders,
        }),
      )
      return
    }
    productDetailsFormValuesRef.current = values

    const selectedCheckoutItemId =
      checkoutCurrentCheckoutSelectedItemIdSelector(store.getState())

    if (
      hasUpsellGroups &&
      !productUpsellIsOutOfStock &&
      !selectedCheckoutItemId
    ) {
      batch(() => {
        dispatch(setProductUpsellModalVisible(true))
        dispatch(setProductDetailsModalVisible(false))
      })
    } else {
      dispatch(setProductDetailsModalVisible(false))
      await handleAddToItemsToCart()
    }
  }

  async function handleUpsellModalSubmit(
    values: ConfigureProductUpsellGroupFormState,
  ) {
    const { selectedUpsellGroups } = values
    const selectedUpsellCount = selectedUpsellGroups.reduce(
      (acc, currentUpSell) => acc + currentUpSell.selectedCount,
      0,
    )
    const hasSelections = selectedUpsellCount > 0
    if (productDetailsFormValuesRef.current) {
      dispatch(setProductUpsellModalVisible(false))
      const shouldAddUpsell = hasSelections
      await handleAddToItemsToCart(shouldAddUpsell ? values : undefined)
    }
  }

  function handleUpsellModalDismiss() {
    dispatch(setProductUpsellModalVisible(false))
    clearSelectedCheckoutItemId()
  }

  function handleProductDetailsModalDismiss() {
    dispatch(setProductDetailsModalVisible(false))
    clearSelectedCheckoutItemId()
  }

  return (
    <>
      <ProductDetailsModal
        onSubmit={handleProductDetailsModalSubmit}
        onDismiss={handleProductDetailsModalDismiss}
      />
      <ProductUpsellModal
        onSubmit={handleUpsellModalSubmit}
        onDismiss={handleUpsellModalDismiss}
      />
    </>
  )
}
